/**
 * filters.tsx — the SHARED filter grammar: one toolbar control, one panel
 * vocabulary, one chip.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: staff-console filters.
 *
 * WHY THIS MODULE EXISTS (2026-09-05). The CRM pipeline grew a proper filter
 * pattern over three CTO passes — a "Filters (n)" button that LOOKS applied, a
 * modal holding a draft, and applied state shown as removable chips. It worked,
 * and then the Patients directory grew a bare inline checkbox for the same idea
 * and the Payments board grew nothing at all. Three staff screens, three
 * different shapes for "narrow what this list shows".
 *
 * The pieces were all sitting in `pipeline/` as module-private functions, so
 * the only way to reuse them was to copy them — which is how the second
 * implementation gets written and the third one after that. They are LIFTED
 * here rather than duplicated: `pipeline/PipelineFilters.tsx` and
 * `pipeline/PipelineTab.tsx` now import the same `FilterTick` / `FilterSection`
 * / `FilterChip` every other screen does, so a change to the grammar lands
 * everywhere at once and the screens cannot drift apart again.
 *
 * ⚠️ WHAT IS DELIBERATELY NOT HERE: the FACETS. This module knows about
 * checkboxes, chips and a button; it knows nothing about stages, entry paths or
 * test records. Each screen owns its own filter STATE type and its own panel,
 * because the facets are the one part that genuinely differs — a generic
 * "filter state" abstraction over three unrelated shapes would be the fork this
 * module exists to prevent, wearing a shared name.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Button } from './kit';

/**
 * THE ONE HEIGHT EVERY CONTROL ON A FILTER TOOLBAR ROW SHARES.
 *
 * 48px, because that is what `CONTROL_STYLE` in ui/kit already gives a
 * TextInput — the row is matched to the input rather than the input squeezed to
 * the buttons' old 44px, so nothing on the bar shrinks and the 44px WCAG 2.5.5
 * floor is cleared with room to spare.
 *
 * WHY A SHARED NUMBER AND NOT AN ALIGNMENT RULE (CTO 2026-09-04, on the
 * pipeline bar: Search, "Filters (n)" and the red × "do not line up"). They
 * were 48, 44 and 44 with `alignItems: 'flex-end'`, so their bottoms agreed and
 * nothing else did — three different heights read as three unrelated things
 * however they are aligned. Give them ONE height and `alignItems: 'center'` and
 * their centres, tops and bottoms all agree at once.
 */
export const CONTROL_ROW_HEIGHT = '48px';

/**
 * An APPLIED filter, shown as a removable chip.
 *
 * ⚠️ CHIP-SHAPED ON PURPOSE, and nothing else on a filter bar is. The applied
 * filter used to render as a ghost button beside a ghost "Clear" button — one
 * was state, the other an action, and nothing distinguished them. A pill with
 * its own × reads as "here is what is on", and the × makes removing THIS one a
 * single obvious act rather than something to go back into the panel for.
 */
export function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span style={chipStyle}>
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove filter: ${label}`} style={chipRemoveStyle}>
        ×
      </button>
    </span>
  );
}

/**
 * The toolbar's filter control: the "Filters (n)" button, plus the danger-toned
 * clear-all × once anything is applied.
 *
 * ⚠️ THE BUTTON MUST LOOK SELECTED, NOT JUST COUNT (2026-08-31, CTO: "if
 * filters applied the filters button should be selected to indicate there are
 * filters applied. and red X indicating you can click on that to remove").
 * A "(2)" suffix on an otherwise unchanged ghost button is a label change, not
 * a state change — on a list that already looks filtered (fewer rows) there is
 * nothing to tell you WHY. Applied borrows the chip palette exactly: tinted
 * fill, primary border, 600 weight, so "this control is on" and "here is what
 * it is set to" read as one thing. `aria-pressed` carries the same fact
 * non-visually, which a count in a label cannot.
 *
 * ⚠️ THE CLEAR IS A SEPARATE CONTROL, deliberately — putting it inside the
 * Filters button would make one target do two opposite things. It is rendered
 * only when there is something to clear, so it never sits there inert. It is
 * DANGER-TONED because it is the only destructive control on the bar (one press
 * drops every applied filter) and must not read like the chips' own × marks,
 * which each remove exactly one thing. The border carries the tone rather than
 * the fill: a solid red block beside the primary-tinted Filters button would
 * out-shout the list it is meant to serve.
 */
export function FiltersTrigger({
  appliedCount,
  onOpen,
  onClearAll,
}: {
  appliedCount: number;
  onOpen: () => void;
  onClearAll: () => void;
}) {
  return (
    <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Button
        type="button"
        variant="ghost"
        aria-pressed={appliedCount > 0}
        style={{
          width: 'auto',
          // One height for the whole row — see CONTROL_ROW_HEIGHT. `height` as
          // well as `minHeight` because the ghost Button's own padding would
          // otherwise decide, and 48 is the number the input sets.
          height: CONTROL_ROW_HEIGHT,
          minHeight: CONTROL_ROW_HEIGHT,
          padding: '0 18px',
          ...(appliedCount > 0
            ? {
                border: '1px solid var(--gps-primary)',
                background: 'var(--gps-primary-light)',
                color: 'var(--gps-text)',
                fontWeight: 600,
              }
            : {}),
        }}
        onClick={onOpen}
      >
        Filters{appliedCount > 0 ? ` (${appliedCount})` : ''}
      </Button>
      {appliedCount > 0 ? (
        <button
          type="button"
          onClick={onClearAll}
          aria-label={`Clear all filters (${appliedCount} applied)`}
          title="Clear all filters"
          style={clearAllButtonStyle}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

/**
 * The row that holds the applied chips, sized to sit INSIDE the toolbar's flex
 * row beside the Filters button.
 *
 * ⚠️ BESIDE THE BUTTON, NEVER ON ITS OWN ROW ABOVE THE CONTENT (2026-09-05).
 * A full-width chip row under the bar pushes the list, the board or the table
 * down by its own height the moment a filter is applied — so the content the
 * user is filtering jumps away from them at the exact moment they act on it,
 * and jumps back when they remove the last chip. The toolbar row already has
 * empty space to the right of the Filters button on every one of these screens;
 * the chips go there, and applying a filter moves nothing below.
 *
 * `flex: '1 1 340px'` + `minWidth: 0` so the chips take the slack and WRAP
 * inside their own box rather than pushing the button off the row; the row's
 * container should be `alignItems: 'flex-start'`, because a wrapped chip block
 * is the one item on the bar allowed to be taller than CONTROL_ROW_HEIGHT.
 */
export function FilterChipRow({ children }: { children: ReactNode }) {
  return (
    <div
      role="group"
      aria-label="Applied filters"
      style={{
        flex: '1 1 340px',
        minWidth: 0,
        minHeight: CONTROL_ROW_HEIGHT,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        alignItems: 'center',
      }}
    >
      {children}
    </div>
  );
}

/** One titled group of ticks inside a filter panel, with an optional total. */
export function FilterSection({
  label,
  hint,
  total,
  children,
}: {
  label: string;
  hint: string;
  total?: number;
  children: ReactNode;
}) {
  return (
    <section aria-label={label} style={{ marginBottom: 'var(--gps-s-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px' }}>
        <h3 style={sectionTitleStyle}>{label}</h3>
        {total !== undefined ? <span style={{ ...filterNumStyle, fontWeight: 700 }}>{total}</span> : null}
      </div>
      <p style={filterNoteStyle}>{hint}</p>
      <div style={{ marginTop: '8px' }}>{children}</div>
    </section>
  );
}

/**
 * One checkbox row.
 *
 * A REAL <input type="checkbox"> inside a <label>, not a styled div with
 * aria-checked: the panel is a form, and the native control brings keyboard
 * reachability, the checked announcement and the browser's own focus ring for
 * free. The count and bar sit OUTSIDE the label text so they do not end up in
 * the accessible name — a caption folded into the name is announced in full
 * every time the control is reached.
 */
export function FilterTick({
  label,
  count,
  share,
  checked,
  describedBy,
  onChange,
}: {
  label: string;
  count?: number;
  share?: number;
  checked: boolean;
  describedBy?: string;
  onChange: () => void;
}) {
  return (
    <label style={tickStyle(checked)}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        aria-describedby={describedBy}
        style={{ width: '18px', height: '18px', flex: 'none', accentColor: 'var(--gps-primary)', cursor: 'pointer' }}
      />
      <span style={{ flex: '1 1 auto', minWidth: 0 }}>{label}</span>
      {share !== undefined && share > 0 ? (
        <span aria-hidden="true" style={barTrackStyle}>
          <span style={{ ...barFillStyle, width: `${Math.max(2, Math.round(share * 100))}%` }} />
        </span>
      ) : null}
      {count !== undefined ? <span style={filterNumStyle}>{count}</span> : null}
    </label>
  );
}

/**
 * The panel footer: a secondary Clear beside the primary Apply.
 *
 * ⚠️ CLEAR IS SECONDARY, APPLY IS PRIMARY, and Clear does NOT close the panel —
 * clearing is usually the first half of "start again", so closing would make
 * the user re-open it to do the second half.
 */
export function FilterFooter({ onClear, onApply }: { onClear: () => void; onApply: () => void }) {
  return (
    <div style={footerStyle}>
      <Button
        type="button"
        variant="ghost"
        style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
        onClick={onClear}
      >
        Clear all filters
      </Button>
      {/* `primary` (brand green), NOT `cta`. The cta variant is the loud orange
          this app reserves for "Speak to a clinician now" and the journey's own
          commit buttons — borrowing it for a filter apply would put the most
          urgent colour on the page on a routine staff action, and dilute it
          where it actually matters. */}
      <Button
        type="button"
        variant="primary"
        style={{ width: 'auto', minHeight: '44px', padding: '10px 22px' }}
        onClick={onApply}
      >
        Apply filters
      </Button>
    </div>
  );
}

export const filterGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '6px',
};

export const filterNoteStyle: CSSProperties = {
  margin: '2px 0 0',
  fontSize: '12.5px',
  lineHeight: 1.45,
  color: 'var(--gps-muted)',
};

const filterNumStyle: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: '13px',
  color: 'var(--gps-muted)',
  minWidth: '2.5rem',
  textAlign: 'right',
};

function tickStyle(on: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 12px',
    minHeight: '44px',
    fontSize: '14px',
    cursor: 'pointer',
    borderRadius: 'var(--gps-r-md)',
    border: on ? '1px solid var(--gps-primary)' : '1px solid var(--gps-border)',
    background: on ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
  };
}

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--gps-text)',
};

const barTrackStyle: CSSProperties = {
  flex: '0 0 70px',
  height: '6px',
  borderRadius: '3px',
  background: 'var(--gps-surface-2)',
  overflow: 'hidden',
};

const barFillStyle: CSSProperties = { display: 'block', height: '100%', background: 'var(--gps-primary)' };

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 6px 5px 12px',
  borderRadius: 'var(--gps-r-pill)',
  border: '1px solid var(--gps-primary)',
  background: 'var(--gps-primary-light)',
  color: 'var(--gps-text)',
  fontSize: '13px',
  fontWeight: 600,
};

const chipRemoveStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  border: 0,
  borderRadius: '50%',
  background: 'transparent',
  color: 'var(--gps-muted)',
  font: 'inherit',
  fontSize: '15px',
  lineHeight: 1,
  cursor: 'pointer',
};

const clearAllButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: CONTROL_ROW_HEIGHT,
  height: CONTROL_ROW_HEIGHT,
  minHeight: CONTROL_ROW_HEIGHT,
  padding: 0,
  borderRadius: 'var(--gps-r-2)',
  border: '1px solid var(--gps-danger-border, var(--gps-danger-text))',
  background: 'transparent',
  color: 'var(--gps-danger-text)',
  font: 'inherit',
  fontSize: '20px',
  lineHeight: 1,
  cursor: 'pointer',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  marginTop: 'var(--gps-s-4)',
  paddingTop: 'var(--gps-s-3)',
  borderTop: '1px solid var(--gps-border)',
};
